import { defineStore } from 'pinia'
import {
  acceptCourierOrderApi,
  fetchCourierActiveOrdersApi,
  fetchCourierCompletedOrdersApi,
  fetchCourierOrderByIdApi,
  markCourierOrderDeliveredApi,
  markCourierOrderPickedUpApi,
  rejectCourierOrderApi,
} from '@/entities/order/api/courierOrdersApi'
import { isCourierOrderFinished } from '@/entities/order/model/courierOrderFlow'
import { joinClientOrderRoom } from '@/entities/order/socket/joins'
import { useCourierStore } from '@/entities/courier/model/useCourierStore'
import { stopCourierLocationTracking } from '@/features/courier-orders/realtime/courierLocationTracking'

export const useCourierOrdersStore = defineStore('courierOrders', {
  state: () => ({
    selectedOrderId: null,
    activeOrder: null,
    isActiveOrderLoading: false,
    isActiveOrderUpdating: false,
    activeOrderError: null,
    orderList: [],
    isOrderListLoading: false,
    orderListError: null,
    hasLoadedOrderList: false,
    completedOrders: [],
    isCompletedOrdersLoading: false,
    completedOrdersError: null,
    hasLoadedCompletedOrders: false,
  }),
  actions: {
    clear() {
      stopCourierLocationTracking()
      this.selectedOrderId = null
      this.activeOrder = null
      this.isActiveOrderLoading = false
      this.isActiveOrderUpdating = false
      this.activeOrderError = null
      this.orderList = []
      this.isOrderListLoading = false
      this.orderListError = null
      this.hasLoadedOrderList = false
      this.completedOrders = []
      this.isCompletedOrdersLoading = false
      this.completedOrdersError = null
      this.hasLoadedCompletedOrders = false
    },
    setSelectedOrderId(orderId) {
      this.selectedOrderId = orderId ? String(orderId) : null
    },
    mergeOrderInList(order) {
      if (!order?.id) {
        return
      }
      const index = this.orderList.findIndex((item) => item.id === order.id)
      if (index >= 0) {
        this.orderList[index] = order
        return
      }
      this.orderList = [...this.orderList, order]
    },
    async fetchOrderList({ force = false } = {}) {
      if (this.isOrderListLoading) {
        return
      }
      if (this.hasLoadedOrderList && !force) {
        return
      }

      this.isOrderListLoading = true
      this.orderListError = null
      try {
        const { items, activeOrderIds } = await fetchCourierActiveOrdersApi()
        this.orderList = items
        this.hasLoadedOrderList = true
        useCourierStore().setActiveOrderIds(activeOrderIds)
      } catch (error) {
        this.orderListError = error
      } finally {
        this.isOrderListLoading = false
      }
    },
    async loadSelectedOrder(orderId, { silent = false } = {}) {
      const id = orderId ?? this.selectedOrderId
      if (!id) {
        this.activeOrder = null
        return
      }

      this.selectedOrderId = String(id)

      if (!silent) {
        this.isActiveOrderLoading = true
        this.activeOrderError = null
      }

      try {
        const order = await fetchCourierOrderByIdApi(id)
        if (!order || isCourierOrderFinished(order)) {
          await this.fetchOrderList({ force: true })
          if (String(this.selectedOrderId) === String(id)) {
            this.activeOrder = null
            this.selectedOrderId = null
          }
          return
        }
        this.activeOrder = order
        this.mergeOrderInList(order)
        joinClientOrderRoom(order.id)
      } catch (error) {
        if (!silent) {
          this.activeOrderError = error
          this.activeOrder = null
        }
      } finally {
        if (!silent) {
          this.isActiveOrderLoading = false
        }
      }
    },
    async syncSelectedOrderFromServer() {
      const orderId = this.selectedOrderId ?? this.activeOrder?.id
      if (!orderId) {
        return
      }
      await this.loadSelectedOrder(orderId, { silent: true })
    },
    async applyMutationResult({ order, activeOrderIds }) {
      const courierStore = useCourierStore()
      const ids = Array.isArray(activeOrderIds) ? activeOrderIds.map(String) : []
      courierStore.setActiveOrderIds(ids)

      if (order && !isCourierOrderFinished(order)) {
        this.activeOrder = order
        this.mergeOrderInList(order)
        joinClientOrderRoom(order.id)
      }

      await this.fetchOrderList({ force: true })

      if (this.selectedOrderId && ids.includes(String(this.selectedOrderId))) {
        return
      }

      this.activeOrder = null
      this.selectedOrderId = null
      await this.fetchCompletedOrders({ force: true })
      if (!ids.length) {
        await courierStore.refreshProfile()
      }
    },
    async fetchCompletedOrders({ force = false } = {}) {
      if (this.isCompletedOrdersLoading) {
        return
      }
      if (this.hasLoadedCompletedOrders && !force) {
        return
      }

      this.isCompletedOrdersLoading = true
      this.completedOrdersError = null
      try {
        const orders = await fetchCourierCompletedOrdersApi()
        this.completedOrders = [...orders].sort(
          (a, b) => new Date(b.updatedAt ?? 0) - new Date(a.updatedAt ?? 0),
        )
        this.hasLoadedCompletedOrders = true
      } catch (error) {
        this.completedOrdersError = error
      } finally {
        this.isCompletedOrdersLoading = false
      }
    },
    async runOrderMutation(mutator) {
      if (!this.activeOrder || this.isActiveOrderUpdating) {
        return
      }

      this.isActiveOrderUpdating = true
      try {
        const result = await mutator(this.activeOrder.id)
        if (result) {
          await this.applyMutationResult(result)
        }
      } catch (error) {
        await this.syncSelectedOrderFromServer()
        await this.fetchOrderList({ force: true })
        throw error
      } finally {
        this.isActiveOrderUpdating = false
      }
    },
    async acceptActiveOrder() {
      await this.runOrderMutation((orderId) => acceptCourierOrderApi(orderId))
    },
    async rejectActiveOrder() {
      await this.runOrderMutation((orderId) => rejectCourierOrderApi(orderId))
    },
    async confirmOrderPickedUp() {
      await this.runOrderMutation((orderId) => markCourierOrderPickedUpApi(orderId))
    },
    async confirmOrderDelivered({ paymentReceived } = {}) {
      await this.runOrderMutation((orderId) =>
        markCourierOrderDeliveredApi(orderId, { paymentReceived }),
      )
    },
    async handleOrderUnassigned(orderId) {
      await this.fetchOrderList({ force: true })

      const id = orderId ? String(orderId) : null
      if (!id || String(this.selectedOrderId) !== id) {
        return
      }

      this.activeOrder = null
      this.selectedOrderId = null
    },
  },
})
